import { create } from "zustand";

import type { CookieAuthAdapter } from "./adapter";
import {
  type AuthCache,
  type AuthError,
  createAnonymousAndSetAuth,
  isStaleOperation,
  type OperationIdAccessor,
  signOutWithAnonymousFallback,
  startOperation,
  useAuthStore,
} from "../auth-store";

interface CookieAuthState {
  adapter: CookieAuthAdapter | null;
  cache: AuthCache | null;
  operationId: number;
  isSigningIn: boolean;
  isSigningUp: boolean;
  isSigningOut: boolean;
}

interface CookieAuthActions {
  initialize: (adapter: CookieAuthAdapter, cache: AuthCache) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signInAnonymous: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  reset: (error?: AuthError) => void;
}

type CookieAuthStore = CookieAuthState & CookieAuthActions;

export const useCookieAuthStore = create<CookieAuthStore>((set, get) => {
  const opAccessor: OperationIdAccessor = {
    getOperationId: () => get().operationId,
    setOperationId: (id) => set({ operationId: id }),
  };

  return {
    adapter: null,
    cache: null,
    operationId: 0,
    isSigningIn: false,
    isSigningUp: false,
    isSigningOut: false,

    initialize: (adapter, cache) => {
      if (get().adapter) {
        return;
      }

      set({ adapter, cache });
      useAuthStore.getState().setInitializing(true);

      const cached = cache.getUser();
      if (cached) {
        useAuthStore.getState().setAuth(cached, "cookie");
      }

      const opId = startOperation(opAccessor);

      adapter.getSession().then(async (result) => {
        if (isStaleOperation(opId, opAccessor)) {
          return;
        }

        const currentCache = get().cache;
        if (!currentCache) {
          return;
        }

        if (result.isOk && result.value) {
          currentCache.setUser(result.value.user);
          useAuthStore.getState().setAuth(result.value.user, "cookie");
        } else {
          currentCache.setUser(null);
          await createAnonymousAndSetAuth(
            () => adapter.signInAnonymous(),
            "cookie",
            opId,
            opAccessor,
            (user) => currentCache.setUser(user)
          );
        }
      });
    },

    signIn: async (email, password) => {
      const { adapter, cache, isSigningIn } = get();
      if (!(adapter && cache) || isSigningIn) {
        return;
      }

      const opId = startOperation(opAccessor);
      set({ isSigningIn: true });
      useAuthStore.getState().setAuthenticating(true);

      const result = await adapter.signIn(email, password);
      if (isStaleOperation(opId, opAccessor)) {
        return;
      }

      set({ isSigningIn: false });
      useAuthStore.getState().setAuthenticating(false);

      if (result.isOk) {
        cache.setUser(result.value.user);
        useAuthStore.getState().setAuth(result.value.user, "cookie");
      } else {
        useAuthStore.getState().setError(result.error);
      }
    },

    signUp: async (email, password, name) => {
      const { adapter, cache, isSigningUp } = get();
      if (!(adapter && cache) || isSigningUp) {
        return;
      }

      const opId = startOperation(opAccessor);
      set({ isSigningUp: true });
      useAuthStore.getState().setAuthenticating(true);

      const result = await adapter.signUp(email, password, name);
      if (isStaleOperation(opId, opAccessor)) {
        return;
      }

      set({ isSigningUp: false });
      useAuthStore.getState().setAuthenticating(false);

      if (result.isOk) {
        cache.setUser(result.value.user);
        useAuthStore.getState().setAuth(result.value.user, "cookie");
      } else {
        useAuthStore.getState().setError(result.error);
      }
    },

    signInAnonymous: async () => {
      const { adapter, cache, isSigningIn } = get();
      if (!(adapter && cache) || isSigningIn) {
        return;
      }

      const opId = startOperation(opAccessor);
      set({ isSigningIn: true });
      useAuthStore.getState().setAuthenticating(true);

      const result = await adapter.signInAnonymous();
      if (isStaleOperation(opId, opAccessor)) {
        return;
      }

      set({ isSigningIn: false });
      useAuthStore.getState().setAuthenticating(false);

      if (result.isOk) {
        cache.setUser(result.value.user);
        useAuthStore.getState().setAuth(result.value.user, "cookie");
      } else {
        useAuthStore.getState().setError(result.error);
      }
    },

    signOut: async () => {
      const { adapter, cache, isSigningOut } = get();
      if (!(adapter && cache) || isSigningOut) {
        return;
      }

      const opId = startOperation(opAccessor);
      set({ isSigningOut: true });

      await signOutWithAnonymousFallback(
        // biome-ignore lint/suspicious/noEmptyBlockStatements: Intentional void return
        () => adapter.signOut().then(() => {}),
        () => adapter.signInAnonymous(),
        "cookie",
        opId,
        opAccessor,
        () => cache.setUser(null),
        (user) => cache.setUser(user)
      );

      if (!isStaleOperation(opId, opAccessor)) {
        set({ isSigningOut: false });
      }
    },

    refreshSession: async () => {
      const { adapter, cache, isSigningIn, isSigningUp, isSigningOut } = get();
      if (!(adapter && cache)) {
        return;
      }
      if (isSigningIn || isSigningUp || isSigningOut) {
        return;
      }

      const opId = startOperation(opAccessor);
      useAuthStore.getState().setRefreshing(true);

      const result = await adapter.getSession();
      if (isStaleOperation(opId, opAccessor)) {
        return;
      }

      useAuthStore.getState().setRefreshing(false);

      if (result.isOk && result.value) {
        cache.setUser(result.value.user);
        useAuthStore.getState().setAuth(result.value.user, "cookie");
      } else {
        cache.setUser(null);
        useAuthStore.getState().clearAuth(result.isErr ? result.error : null);
      }
    },

    reset: (error) => {
      const { cache } = get();
      cache?.setUser(null);
      set({ isSigningIn: false, isSigningUp: false, isSigningOut: false });
      useAuthStore.getState().clearAuth(error ?? null);
    },
  };
});
